import { Link, useLocation } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Home, Calendar, MapPin, Handshake, Shield, Sun, Moon, Menu, X, LogIn, LogOut, User, Info } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import logoImage from "@/assets/logo-image.png";
import logoFont from "@/assets/logo-font.png";
import logoFontWhite from "@/assets/logo-font-white.png";
import { useTheme } from "@/hooks/useTheme";
import { useState } from "react";

const navItems = [
  { to: "/", label: "Home", icon: Home },
  { to: "/events", label: "Events", icon: Calendar },
  { to: "/geography", label: "Geography", icon: MapPin },
  { to: "/partnership", label: "Partnership", icon: Handshake },
  { to: "/about", label: "About", icon: Info },
];

export const Header = () => {
  const { activeTheme, setActiveTheme } = useTheme();
  const { user, isHost, displayName, setAuthModalOpen, signOut } = useAuth();
  const location = useLocation();
  const [mobileOpen, setMobileOpen] = useState(false);
  const isDark = activeTheme === "dark-blue-violet";

  const isActive = (path: string) =>
    path === "/" ? location.pathname === "/" : location.pathname.startsWith(path);

  return (
    <header className="border-b border-border/50 bg-background sticky top-0 z-50">
      <div className="container mx-auto px-4 md:px-8">
        <nav className="flex items-center justify-between h-14 md:h-16">
          {/* Logo */}
          <Link to="/" className="flex-shrink-0 flex items-center gap-2" onClick={() => setMobileOpen(false)}>
            <img
              src={logoImage}
              alt="Quizrun"
              className="h-7 md:h-9 w-auto transition-opacity hover:opacity-80"
            />
            <img
              src={logoFont}
              alt=""
              className="h-7 md:h-9 w-auto hover:opacity-80 transition-opacity dark:hidden"
            />
            <img
              src={logoFontWhite}
              alt=""
              className="h-7 md:h-9 w-auto hover:opacity-80 transition-opacity hidden dark:block"
            />
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center gap-1">
            {navItems.map((item) => (
              <Button
                key={item.to}
                variant="ghost"
                size="sm"
                asChild
                className={`text-sm font-medium transition-colors ${
                  isActive(item.to)
                    ? "text-foreground bg-muted"
                    : "text-muted-foreground hover:text-foreground"
                }`}
              >
                <Link to={item.to}>
                  <item.icon className="h-4 w-4 mr-1.5" aria-hidden="true" />
                  {item.label}
                </Link>
              </Button>
            ))}
            {isHost && (
              <Button
                variant="ghost"
                size="sm"
                asChild
                className={`text-sm font-medium transition-colors ${
                  isActive("/admin")
                    ? "text-foreground bg-muted"
                    : "text-muted-foreground hover:text-foreground"
                }`}
              >
                <Link to="/admin">
                  <Shield className="h-4 w-4 mr-1.5" aria-hidden="true" />
                  Admin
                </Link>
              </Button>
            )}

            {/* Theme toggle */}
            <Button
              variant="ghost"
              size="icon"
              className="ml-2 text-muted-foreground hover:text-foreground"
              onClick={() => setActiveTheme(isDark ? "light" : "dark-blue-violet")}
              aria-label={isDark ? "Switch to light mode" : "Switch to dark mode"}
            >
              {isDark ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />}
            </Button>

            {/* Auth */}
            {user ? (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="ml-1 rounded-full focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2"
                    aria-label="Open user menu"
                  >
                    <span className="flex h-7 w-7 items-center justify-center rounded-full bg-brand-orange/15 text-brand-orange text-sm font-semibold">
                      {(displayName || user.email || "?").trim().charAt(0).toUpperCase()}
                    </span>
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-48">
                  <div className="px-2 py-1.5">
                    <p className="text-sm font-medium">{displayName || "User"}</p>
                    <p className="text-xs text-muted-foreground truncate">{user.email}</p>
                  </div>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem asChild>
                    <Link to="/profile">
                      <User className="h-4 w-4 mr-2" aria-hidden="true" />
                      Profile
                    </Link>
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={signOut}>
                    <LogOut className="h-4 w-4 mr-2" aria-hidden="true" />
                    Sign out
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            ) : (
              <Button
                variant="ghost"
                size="sm"
                className="ml-2 text-sm text-muted-foreground hover:text-foreground"
                onClick={() => setAuthModalOpen(true)}
              >
                <LogIn className="h-4 w-4 mr-1.5" aria-hidden="true" />
                Sign in
              </Button>
            )}
          </div>

          {/* Mobile: theme toggle + hamburger */}
          <div className="flex md:hidden items-center gap-1">
            <Button
              variant="ghost"
              size="icon"
              className="h-9 w-9 text-muted-foreground"
              onClick={() => setActiveTheme(isDark ? "light" : "dark-blue-violet")}
              aria-label={isDark ? "Switch to light mode" : "Switch to dark mode"}
            >
              {isDark ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />}
            </Button>
            <Button
              variant="ghost"
              size="icon"
              className="h-9 w-9 text-muted-foreground"
              onClick={() => setMobileOpen(!mobileOpen)}
              aria-label="Toggle menu"
            >
              {mobileOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
            </Button>
          </div>
        </nav>

        {/* Mobile menu overlay */}
        {mobileOpen && (
          <div className="md:hidden border-t border-border pb-4 pt-2 animate-in slide-in-from-top-2 duration-200">
            <div className="flex flex-col gap-1">
              {navItems.map((item) => (
                <Button
                  key={item.to}
                  variant="ghost"
                  asChild
                  className={`w-full justify-start text-sm font-medium ${
                    isActive(item.to)
                      ? "text-foreground bg-muted"
                      : "text-muted-foreground"
                  }`}
                >
                  <Link to={item.to} onClick={() => setMobileOpen(false)}>
                    <item.icon className="h-4 w-4 mr-2" aria-hidden="true" />
                    {item.label}
                  </Link>
                </Button>
              ))}
              {isHost && (
                <Button
                  variant="ghost"
                  asChild
                  className={`w-full justify-start text-sm font-medium ${
                    isActive("/admin")
                      ? "text-foreground bg-muted"
                      : "text-muted-foreground"
                  }`}
                >
                  <Link to="/admin" onClick={() => setMobileOpen(false)}>
                    <Shield className="h-4 w-4 mr-2" aria-hidden="true" />
                    Admin
                  </Link>
                </Button>
              )}

              {/* Mobile auth */}
              <div className="border-t border-border mt-2 pt-2">
                {user ? (
                  <div className="flex flex-col gap-1">
                    <div className="flex items-center gap-2 px-2 py-1">
                      <span className="flex h-6 w-6 items-center justify-center rounded-full bg-brand-orange/15 text-brand-orange text-xs font-semibold">
                        {(displayName || user.email || "?").trim().charAt(0).toUpperCase()}
                      </span>
                      <span className="text-sm text-muted-foreground truncate">
                        {displayName || user.email}
                      </span>
                    </div>
                    <Button variant="ghost" asChild className="w-full justify-start text-sm font-medium text-muted-foreground">
                      <Link to="/profile" onClick={() => setMobileOpen(false)}>
                        <User className="h-4 w-4 mr-2" aria-hidden="true" />
                        Profile
                      </Link>
                    </Button>
                    <Button
                      variant="ghost"
                      className="w-full justify-start text-sm font-medium text-muted-foreground"
                      onClick={() => { signOut(); setMobileOpen(false); }}
                    >
                      <LogOut className="h-4 w-4 mr-2" aria-hidden="true" />
                      Sign out
                    </Button>
                  </div>
                ) : (
                  <Button
                    variant="ghost"
                    className="w-full justify-start text-sm font-medium text-muted-foreground"
                    onClick={() => { setAuthModalOpen(true); setMobileOpen(false); }}
                  >
                    <LogIn className="h-4 w-4 mr-2" aria-hidden="true" />
                    Sign in
                  </Button>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    </header>
  );
};
